// @flow
// Shared by HUDContractRow's helperText and the Property Info Save gate.
import messages from './messages';
import HUD_SUBSIDY_CODES from '../../../../../constants/hudSubsidyCodes';

export type HUDContractIdentifiers = {
  ownerDUNSNumber?: ?string,
  parentCompanyDUNSNumber?: ?string,
  ownerTIN?: ?string,
  parentCompanyTIN?: ?string,
};

export type HUDContractIdentifierErrors = {
  ownerDUNSNumber?: Object,
  parentCompanyDUNSNumber?: Object,
  ownerTIN?: Object,
  parentCompanyTIN?: Object,
};

// Never trimmed: a whitespace-only value is not blank, so it fails the format check.
const isBlank = (value: ?string): boolean =>
  value === null || value === undefined || value === '';

const isAlphanumeric = (value: string): boolean => /^[a-zA-Z0-9]+$/.test(value);

const getFormatError = (value: ?string): ?Object => {
  if (isBlank(value)) return null;
  if (!isAlphanumeric(value)) return messages.identifierAlphanumericOnly;
  if (value.length !== 9) return messages.identifierExactlyNine;
  return null;
};

export const TRACS_IMAX_ERRORS = {
  alphanumericOnly: messages.tracsiMaxIdAlphanumericOnly,
  mustBeginWithTRACM: messages.tracsiMaxIdMustBeginWithTRACM,
  exactlyTen: messages.tracsiMaxIdExactlyTen,
};

export const CA_IMAX_ERRORS = {
  alphanumericOnly: messages.caiMaxIdAlphanumericOnly,
  mustBeginWithTRACM: messages.caiMaxIdMustBeginWithTRACM,
  exactlyTen: messages.caiMaxIdExactlyTen,
};

// TRACS and CA iMAX IDs are optional; when set they must be TRACM followed by five alphanumerics.
export const getIMaxIdError = (
  value: ?string,
  errors: typeof TRACS_IMAX_ERRORS = TRACS_IMAX_ERRORS,
): ?Object => {
  // Whitespace-only is blank (submitted as null); spaces around a real ID are still rejected below.
  if (isBlank(value?.trim())) return null;
  const id = String(value).toUpperCase();
  if (!isAlphanumeric(id)) return errors.alphanumericOnly;
  if (!id.startsWith('TRACM')) return errors.mustBeginWithTRACM;
  if (id.length !== 10) return errors.exactlyTen;
  return null;
};

export const HUD_PROJECT_NUMBER_REQUIRED_SUBSIDY_CODES = [
  HUD_SUBSIDY_CODES.RENT_SUPPLEMENT,
  HUD_SUBSIDY_CODES.RAP,
  HUD_SUBSIDY_CODES.SECTION_236_PRIMARY,
  HUD_SUBSIDY_CODES.BMIR_PRIMARY,
  HUD_SUBSIDY_CODES.SECTION_202_PRAC,
  HUD_SUBSIDY_CODES.SECTION_811_PRAC,
  HUD_SUBSIDY_CODES.SECTION_202_162,
];

export const getHUDProjectNumberError = (
  value: ?string,
  primarySubsidyTypeCode: ?string,
): ?Object => {
  const projectNumber = value?.trim().toUpperCase();
  if (!projectNumber) {
    return HUD_PROJECT_NUMBER_REQUIRED_SUBSIDY_CODES.includes(
      primarySubsidyTypeCode ?? '',
    )
      ? messages.hudProjectNumberRequired
      : null;
  }
  if (/^800\d{6}$/.test(projectNumber)) {
    return messages.hudProjectNumberIsPropertyId;
  }
  if (projectNumber === '0000FMHA') {
    return messages.hudProjectNumberPlaceholderNotAllowed;
  }
  if (!/^[A-Z0-9]{8}$/.test(projectNumber)) {
    return messages.hudProjectNumberExactlyEight;
  }
  return null;
};

export const getHUDPropertyIdError = (value: ?string): ?Object => {
  const propertyId = value?.trim();
  if (!propertyId) return null;
  if (!/^\d+$/.test(propertyId)) return messages.hudPropertyIdDigitsOnly;
  if (propertyId.length > 10) return messages.hudPropertyIdMaxTen;
  return null;
};

// A stored value only blocks Save once edited, so untouched legacy data never fails an unrelated save.
export const hasEditedHUDSetupIdentifierError = (
  setup: ?Object,
  originalSetup: ?Object,
  hudTracsIntegration: ?boolean,
): boolean =>
  [
    ['HUDiMaxNumber', (value) => getIMaxIdError(value)],
    ['HUDCAiMaxNumber', (value) => getIMaxIdError(value, CA_IMAX_ERRORS)],
    ...(hudTracsIntegration
      ? [
          ['HUDProjectNumber', getHUDProjectNumberError],
          ['HUDPropertyId', getHUDPropertyIdError],
        ]
      : []),
  ].some(
    ([field, getError]) =>
      (setup?.[field] ?? null) !== (originalSetup?.[field] ?? null) &&
      Boolean(getError(setup?.[field], setup?.hudPrimarySubsidyType?.code)),
  );

// Each DUNS #/TIN pair is all-or-nothing, and Parent Company identifiers require
// the Owner pair. Format errors take precedence over required errors.
export const getHUDContractIdentifierErrors = (
  contract: ?HUDContractIdentifiers,
): HUDContractIdentifierErrors => {
  const {
    ownerDUNSNumber,
    parentCompanyDUNSNumber,
    ownerTIN,
    parentCompanyTIN,
  } = contract || {};

  const errors: HUDContractIdentifierErrors = {};

  const ownerDUNSNumberFormatError = getFormatError(ownerDUNSNumber);
  const parentCompanyDUNSNumberFormatError = getFormatError(
    parentCompanyDUNSNumber,
  );
  const ownerTINFormatError = getFormatError(ownerTIN);
  const parentCompanyTINFormatError = getFormatError(parentCompanyTIN);

  const isParentCompanyPopulated =
    !isBlank(parentCompanyDUNSNumber) || !isBlank(parentCompanyTIN);

  if (ownerDUNSNumberFormatError) {
    errors.ownerDUNSNumber = ownerDUNSNumberFormatError;
  } else if (isBlank(ownerDUNSNumber)) {
    if (!isBlank(ownerTIN)) {
      errors.ownerDUNSNumber = messages.requiredByOwnerTIN;
    } else if (isParentCompanyPopulated) {
      errors.ownerDUNSNumber = messages.requiredByParentCompanyIdentifiers;
    }
  }

  if (ownerTINFormatError) {
    errors.ownerTIN = ownerTINFormatError;
  } else if (isBlank(ownerTIN)) {
    if (!isBlank(ownerDUNSNumber)) {
      errors.ownerTIN = messages.requiredByOwnerDUNSNumber;
    } else if (isParentCompanyPopulated) {
      errors.ownerTIN = messages.requiredByParentCompanyIdentifiers;
    }
  }

  if (parentCompanyDUNSNumberFormatError) {
    errors.parentCompanyDUNSNumber = parentCompanyDUNSNumberFormatError;
  } else if (isBlank(parentCompanyDUNSNumber) && !isBlank(parentCompanyTIN)) {
    errors.parentCompanyDUNSNumber = messages.requiredByParentCompanyTIN;
  }

  if (parentCompanyTINFormatError) {
    errors.parentCompanyTIN = parentCompanyTINFormatError;
  } else if (isBlank(parentCompanyTIN) && !isBlank(parentCompanyDUNSNumber)) {
    errors.parentCompanyTIN = messages.requiredByParentCompanyDUNSNumber;
  }

  return errors;
};

export const hasHUDContractIdentifierErrors = (
  contract: ?HUDContractIdentifiers,
): boolean => Object.keys(getHUDContractIdentifierErrors(contract)).length > 0;

const OWNER_DUNS_TRACS_SUBSIDY_CODES = [
  HUD_SUBSIDY_CODES.SECTION_8,
  HUD_SUBSIDY_CODES.RENT_SUPPLEMENT,
  HUD_SUBSIDY_CODES.RAP,
  HUD_SUBSIDY_CODES.SECTION_202_PRAC,
  HUD_SUBSIDY_CODES.SECTION_811_PRAC,
];

// Non-blocking: never feeds the Save gate. Pass the primary subsidy type code only
// for primary contract rows; a secondary row has no code, so it never warns.
export const shouldWarnMissingOwnerDUNS = (
  primarySubsidyTypeCode: ?string,
  contract: ?HUDContractIdentifiers,
): boolean =>
  OWNER_DUNS_TRACS_SUBSIDY_CODES.includes(primarySubsidyTypeCode ?? '') &&
  isBlank(contract?.ownerDUNSNumber);
